import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { castDraft, WritableDraft } from 'immer';
import { v4 as uuidv4 } from 'uuid';

import {
  DEFAULT_STUDY_DAYS,
  EMPTY_ACT_SCORES,
  EMPTY_SAT_SCORES,
  HOURS_PER_DAY,
  MAX_SCHOOLS,
} from './satActBooster.constants';
import {
  ActScores,
  Blackout,
  BoosterDraft,
  BoosterTargets,
  ReportFacts,
  SatScores,
  SchoolSelection,
  StepKey,
  Subscore,
} from './satActBooster.types';
import { logout } from '../session/logoutThunk';
import type { RootState } from '../store';

type SatActBoosterState = Readonly<{
  draft: BoosterDraft;
  /** In the slice so it survives navigation; reset only by restart/logout. */
  step: StepKey;
  reportFacts: ReportFacts | null;
  /** Report markdown captured from the chat, persisted at completion. */
  reportContent: string | null;
  /** Chat messages present when this run queued its report request; null until it is queued. */
  reportRequestFloor: number | null;
  /** Phase-1 submission created at the report step; finalized at completion. */
  submissionId: string | null;
  /** In the slice so it survives navigation; cleared only by restart/logout, so a remount can't re-run resume over a live run. */
  resumeSettled: boolean;
  /** In the slice so stepping away and back can't reset it and re-run the opening analysis. */
  targetAnalysisFired: boolean;
  /** Schools added but not yet analyzed, oldest first. Only drained while the step is mounted. */
  pendingAnalysisNames: readonly string[];
  /** ISO date of the plan day opened in the Daily Practice tab; null when none is selected. */
  selectedDayDate: string | null;
  /** In-progress quiz state per day, so navigating away mid-quiz (or to results) returns exactly where you were. */
  quizProgressByDay: Record<string, QuizProgress>;
}>;

/** One day's in-progress quiz: locked answers (question index → chosen option), position, and whether results show. */
export type QuizProgress = Readonly<{
  answers: Record<number, number>;
  currentIndex: number;
  showResults: boolean;
}>;

const emptyQuizProgress: QuizProgress = { answers: {}, currentIndex: 0, showResults: false };

const initialDraft: BoosterDraft = {
  firstName: '',
  expectedGraduationYear: null,
  programStartYear: null,
  zip: '',
  school: '',
  whichTests: null,
  actScores: EMPTY_ACT_SCORES,
  satScores: EMPTY_SAT_SCORES,
  subscores: [],
  ownsSubscores: true,
  targets: {
    targetTest: null,
    targetScore: null,
    testDate: null,
    startDate: null,
    hoursPerDay: HOURS_PER_DAY.default,
    focusPref: 'single',
    studyDays: [...DEFAULT_STUDY_DAYS],
    schools: [],
    blackouts: [],
  },
};

const initialState: SatActBoosterState = {
  draft: initialDraft,
  step: 'INTAKE',
  reportFacts: null,
  reportContent: null,
  reportRequestFloor: null,
  submissionId: null,
  resumeSettled: false,
  targetAnalysisFired: false,
  pendingAnalysisNames: [],
  selectedDayDate: null,
  quizProgressByDay: {},
};

export type IntakeFields = Partial<
  Pick<
    BoosterDraft,
    'firstName' | 'expectedGraduationYear' | 'programStartYear' | 'zip' | 'school' | 'whichTests'
  >
>;

/**
 * Drop a report that no longer describes the draft. The report is derived from the scores, so any
 * edit to them makes it stale — and it can only be recomputed on the seam into Targets, which is
 * guarded on there being no report. Clearing it re-arms that guard; the recompute anchors a fresh
 * floor and streams a new report.
 */
const invalidateReport = (state: WritableDraft<SatActBoosterState>) => {
  state.reportFacts = null;
  state.reportContent = null;
  state.reportRequestFloor = null;
};

/**
 * The server rejects a target score from the previous test's scale, and a sitting date belongs to
 * one test's calendar.
 */
const clearTestScopedTargets = (state: WritableDraft<SatActBoosterState>) => {
  state.draft.targets.targetTest = null;
  state.draft.targets.targetScore = null;
  state.draft.targets.testDate = null;
};

export const satActBoosterSlice = createSlice({
  name: 'satActBooster',
  initialState,
  reducers: {
    updateIntake: (state, action: PayloadAction<IntakeFields>) => {
      const previousWhichTests = state.draft.whichTests;

      state.draft = { ...state.draft, ...action.payload };

      const nextWhichTests = action.payload.whichTests;

      // A dropped test's scores stay — the submission withholds them instead. The report and
      // test-scoped targets go because both describe a single resolved test.
      if (nextWhichTests !== undefined && nextWhichTests !== previousWhichTests) {
        invalidateReport(state);
        clearTestScopedTargets(state);
      }
    },
    updateActScores: (state, action: PayloadAction<Partial<ActScores>>) => {
      state.draft.actScores = { ...state.draft.actScores, ...action.payload };
      invalidateReport(state);
      // A "Both" taker's test is resolved server-side, so a score edit can move it to the other scale.
      if (state.draft.whichTests === 'Both') clearTestScopedTargets(state);
    },
    updateSatScores: (state, action: PayloadAction<Partial<SatScores>>) => {
      state.draft.satScores = { ...state.draft.satScores, ...action.payload };
      invalidateReport(state);
      if (state.draft.whichTests === 'Both') clearTestScopedTargets(state);
    },
    setSubscore: (state, action: PayloadAction<Subscore>) => {
      const { categoryCode, percentCorrect, valueLow, valueHigh } = action.payload;
      const existing = state.draft.subscores.findIndex((s) => s.categoryCode === categoryCode);
      const isEmpty = percentCorrect === null && valueLow === null && valueHigh === null;

      if (existing !== -1) {
        if (isEmpty) {
          state.draft.subscores.splice(existing, 1);
        } else {
          state.draft.subscores[existing] = action.payload;
        }
      } else if (!isEmpty) {
        state.draft.subscores.push(action.payload);
      }

      // `ownsSubscores` is deliberately left alone: claiming it here would save this partial
      // view over the whole breakdown. The edit shows locally; it just can't overwrite.
      invalidateReport(state);
    },
    updateTargets: (state, action: PayloadAction<Partial<BoosterTargets>>) => {
      state.draft.targets = { ...state.draft.targets, ...action.payload };
    },
    /** Replace the whole draft — used to rebuild it from a saved submission on resume. */
    hydrateBoosterDraft: (state, action: PayloadAction<BoosterDraft>) => {
      state.draft = castDraft(action.payload);
    },
    setStudyDays: (state, action: PayloadAction<number[]>) => {
      state.draft.targets.studyDays = action.payload;
    },
    addSchool: (state, action: PayloadAction<SchoolSelection>) => {
      const { schools } = state.draft.targets;
      const alreadyPicked = schools.some((s) => s.name === action.payload.name);

      if (!alreadyPicked && schools.length < MAX_SCHOOLS) {
        schools.push(action.payload);
        state.pendingAnalysisNames = [...state.pendingAnalysisNames, action.payload.name];
      }
    },
    removeSchool: (state, action: PayloadAction<string>) => {
      state.draft.targets.schools = state.draft.targets.schools.filter(
        (s) => s.name !== action.payload,
      );
      state.pendingAnalysisNames = state.pendingAnalysisNames.filter(
        (name) => name !== action.payload,
      );
    },
    addBlackout: (state, action: PayloadAction<Omit<Blackout, 'id'>>) => {
      state.draft.targets.blackouts.push({ id: uuidv4(), ...action.payload });
    },
    removeBlackout: (state, action: PayloadAction<string>) => {
      state.draft.targets.blackouts = state.draft.targets.blackouts.filter(
        (b) => b.id !== action.payload,
      );
    },
    setBoosterStep: (state, action: PayloadAction<StepKey>) => {
      state.step = action.payload;
    },
    setBoosterReportFacts: (state, action: PayloadAction<ReportFacts | null>) => {
      state.reportFacts = castDraft(action.payload);
    },
    setBoosterReportContent: (state, action: PayloadAction<string | null>) => {
      state.reportContent = action.payload;
    },
    markTargetAnalysisFired: (state) => {
      state.targetAnalysisFired = true;
    },
    shiftPendingAnalysis: (state) => {
      state.pendingAnalysisNames = state.pendingAnalysisNames.slice(1);
    },
    clearPendingAnalysis: (state) => {
      state.pendingAnalysisNames = [];
    },
    setBoosterReportRequestFloor: (state, action: PayloadAction<number>) => {
      state.reportRequestFloor = action.payload;
    },
    setBoosterSubmissionId: (state, action: PayloadAction<string | null>) => {
      state.submissionId = action.payload;
    },
    setBoosterResumeSettled: (state, action: PayloadAction<boolean>) => {
      state.resumeSettled = action.payload;
    },
    /** The plan day the Daily Practice tab reads; set on the output page when a calendar day is opened. */
    setBoosterSelectedDay: (state, action: PayloadAction<string | null>) => {
      state.selectedDayDate = action.payload;
    },
    /** Lock the chosen option for one question of a day's quiz (submit). */
    recordQuizAnswer: (
      state,
      action: PayloadAction<{ dayDate: string; questionIndex: number; optionIndex: number }>,
    ) => {
      const { dayDate, questionIndex, optionIndex } = action.payload;
      const progress = state.quizProgressByDay[dayDate] ?? { ...emptyQuizProgress, answers: {} };

      state.quizProgressByDay[dayDate] = {
        ...progress,
        answers: { ...progress.answers, [questionIndex]: optionIndex },
      };
    },
    /** Move the quiz's current-question pointer for a day. */
    setQuizIndex: (state, action: PayloadAction<{ dayDate: string; index: number }>) => {
      const { dayDate, index } = action.payload;
      const progress = state.quizProgressByDay[dayDate] ?? { ...emptyQuizProgress, answers: {} };

      state.quizProgressByDay[dayDate] = { ...progress, currentIndex: index };
    },
    /** Flip a day's quiz to (or from) its results view. */
    setQuizShowResults: (
      state,
      action: PayloadAction<{ dayDate: string; showResults: boolean }>,
    ) => {
      const { dayDate, showResults } = action.payload;
      const progress = state.quizProgressByDay[dayDate] ?? { ...emptyQuizProgress, answers: {} };

      state.quizProgressByDay[dayDate] = { ...progress, showResults };
    },
    /**
     * Reset the whole wizard to a fresh run, with the resume already declined: a run the student
     * just finished or walked away from must not be rebuilt over the clean slate.
     */
    startFreshRun: () => ({ ...initialState, resumeSettled: true }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  updateIntake,
  updateActScores,
  updateSatScores,
  setSubscore,
  updateTargets,
  hydrateBoosterDraft,
  setStudyDays,
  addSchool,
  removeSchool,
  addBlackout,
  removeBlackout,
  setBoosterStep,
  setBoosterReportFacts,
  setBoosterReportContent,
  markTargetAnalysisFired,
  shiftPendingAnalysis,
  clearPendingAnalysis,
  setBoosterReportRequestFloor,
  setBoosterSubmissionId,
  setBoosterResumeSettled,
  setBoosterSelectedDay,
  recordQuizAnswer,
  setQuizIndex,
  setQuizShowResults,
  startFreshRun,
} = satActBoosterSlice.actions;

export const selectBoosterDraft = (state: RootState): BoosterDraft => state.satActBooster.draft;
export const selectBoosterTargets = (state: RootState): BoosterTargets =>
  state.satActBooster.draft.targets;
export const selectBoosterStep = (state: RootState): StepKey => state.satActBooster.step;
export const selectBoosterReportFacts = (state: RootState): ReportFacts | null =>
  state.satActBooster.reportFacts;
export const selectBoosterReportContent = (state: RootState): string | null =>
  state.satActBooster.reportContent;
export const selectBoosterTargetAnalysisFired = (state: RootState): boolean =>
  state.satActBooster.targetAnalysisFired;
export const selectBoosterPendingAnalysisNames = (state: RootState): readonly string[] =>
  state.satActBooster.pendingAnalysisNames;
export const selectBoosterReportRequestFloor = (state: RootState): number | null =>
  state.satActBooster.reportRequestFloor;
export const selectBoosterSubmissionId = (state: RootState): string | null =>
  state.satActBooster.submissionId;
export const selectBoosterResumeSettled = (state: RootState): boolean =>
  state.satActBooster.resumeSettled;
export const selectBoosterSelectedDay = (state: RootState): string | null =>
  state.satActBooster.selectedDayDate;
/** One day's in-progress quiz state, or undefined before the student touches that day's quiz. */
export const selectBoosterQuizProgress =
  (dayDate: string) =>
  (state: RootState): QuizProgress | undefined =>
    state.satActBooster.quizProgressByDay[dayDate];
/** True before the run starts; resume may only decide from this state, never over a run in flight. */
export const selectBoosterIsPristine = (state: RootState): boolean => {
  const s = state.satActBooster;

  return (
    s.step === 'INTAKE' &&
    s.reportFacts === null &&
    s.reportContent === null &&
    s.submissionId === null
  );
};
